import React from "react";
import { Box, CircularProgress, LinearProgress, Typography } from "@mui/material";

/** Sources listed under the screening heading (mockup / #10119). */
export const BULK_SCREENING_SOURCES_LABEL =
  "Checking against OIG LEIE, SAM, State Medicaid (50), OFAC SDN, TRICARE, Medicare Opt-Out.";

/** Heading: "Screening [N] records…" */
export function buildBulkScreeningHeading(recordCount: number): string {
  const n = Math.max(0, Math.floor(Number(recordCount) || 0));
  return `Screening ${n} records…`;
}

export function buildBulkProgressLabel(
  checkedCount: number,
  recordCount: number,
): string {
  const total = Math.max(0, Math.floor(Number(recordCount) || 0));
  const checked = Math.min(
    total,
    Math.max(0, Math.floor(Number(checkedCount) || 0)),
  );
  return `${checked} of ${total} checked`;
}

export type BulkProcessingViewProps = {
  /** Total records being screened (valid rows). */
  recordCount: number;
  /** How many rows have finished screening so far. */
  checkedCount?: number;
};

/**
 * Bulk check processing stage (#10119).
 * Shown after "Start check" in the upload modal; no actions while running.
 */
export default function BulkProcessingView({
  recordCount,
  checkedCount = 0,
}: BulkProcessingViewProps) {
  const total = Math.max(0, Math.floor(Number(recordCount) || 0));
  const checked = Math.min(
    total,
    Math.max(0, Math.floor(Number(checkedCount) || 0)),
  );
  const progressPct = total > 0 ? (checked / total) * 100 : 0;

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="bulk-processing-view"
      sx={{
        maxWidth: 560,
        width: "100%",
        margin: "60px auto",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <CircularProgress
        size={40}
        thickness={4}
        sx={{ color: "#0891b2" }}
        aria-label="Screening in progress"
      />
      <Typography
        component="h2"
        sx={{
          fontWeight: 600,
          fontSize: "16px",
          color: "#0f172a",
          letterSpacing: "-0.01em",
        }}
      >
        {buildBulkScreeningHeading(total)}
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "#64748b", maxWidth: 480 }}>
        {BULK_SCREENING_SOURCES_LABEL}
      </Typography>
      <Box sx={{ width: "100%", maxWidth: 320 }}>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          aria-valuenow={Math.round(progressPct)}
          aria-valuemin={0}
          aria-valuemax={100}
          sx={{
            height: 6,
            borderRadius: 999,
            backgroundColor: "#e2e8f0",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#0891b2",
              borderRadius: 999,
            },
          }}
        />
      </Box>
      <Typography sx={{ fontSize: "12px", color: "#64748b" }}>
        {buildBulkProgressLabel(checked, total)}
      </Typography>
    </Box>
  );
}
